import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { Maximize2, MessageCircle, Minimize2, Minus } from "lucide-react";

type Props = {
  floating: boolean;
  label: string;
  onChangeFloating: (floating: boolean) => void;
  children: ReactNode;
};

type FloatingRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type LauncherPosition = {
  left: number;
  top: number;
};

type ResizeCorner = "nw" | "ne" | "sw" | "se";

type ResizeState = {
  corner: ResizeCorner;
  pointerId: number;
  startX: number;
  startY: number;
  rect: FloatingRect;
};

type LauncherDragState = {
  pointerId: number;
  startX: number;
  startY: number;
  startLeft: number;
  startTop: number;
  moved: boolean;
};

const FLOATING_MARGIN = 16;
const MIN_FLOATING_WIDTH = 440;
const MIN_FLOATING_HEIGHT = 420;
const DEFAULT_FLOATING_WIDTH = MIN_FLOATING_WIDTH;
const DEFAULT_FLOATING_HEIGHT = MIN_FLOATING_HEIGHT;
const LAUNCHER_SIZE = 56;
const LAUNCHER_MARGIN = 18;
const LAUNCHER_DRAG_THRESHOLD = 4;
const LAUNCHER_DEFAULT_RIGHT_OFFSET = 72;
const LAUNCHER_DEFAULT_BOTTOM_OFFSET = 50;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function sidebarBoundary() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }

  const sidebar = document.querySelector<HTMLElement>("aside.motion-slide-left");
  if (sidebar) {
    return Math.max(0, sidebar.getBoundingClientRect().right);
  }

  const collapsed = localStorage.getItem("inpronexus.sidebar.collapsed") === "true";
  return collapsed ? 82 : 270;
}

function fitRectToViewport(rect: FloatingRect): FloatingRect {
  if (typeof window === "undefined") return rect;

  const maxWidth = Math.max(280, window.innerWidth - FLOATING_MARGIN * 2);
  const maxHeight = Math.max(320, window.innerHeight - FLOATING_MARGIN * 2);
  const width = clamp(rect.width, Math.min(MIN_FLOATING_WIDTH, maxWidth), maxWidth);
  const height = clamp(rect.height, Math.min(MIN_FLOATING_HEIGHT, maxHeight), maxHeight);

  return {
    width,
    height,
    left: clamp(rect.left, FLOATING_MARGIN, window.innerWidth - width - FLOATING_MARGIN),
    top: clamp(rect.top, FLOATING_MARGIN, window.innerHeight - height - FLOATING_MARGIN),
  };
}

function initialFloatingRect(): FloatingRect {
  if (typeof window === "undefined") {
    return {
      left: FLOATING_MARGIN,
      top: FLOATING_MARGIN,
      width: DEFAULT_FLOATING_WIDTH,
      height: DEFAULT_FLOATING_HEIGHT,
    };
  }

  const width = Math.min(DEFAULT_FLOATING_WIDTH, window.innerWidth - FLOATING_MARGIN * 2);
  const height = Math.min(DEFAULT_FLOATING_HEIGHT, window.innerHeight - FLOATING_MARGIN * 2);

  return {
    width,
    height,
    left: Math.max(FLOATING_MARGIN, window.innerWidth - width - FLOATING_MARGIN),
    top: Math.max(FLOATING_MARGIN, window.innerHeight - height - FLOATING_MARGIN),
  };
}

function fitLauncherToViewport(position: LauncherPosition): LauncherPosition {
  if (typeof window === "undefined") return position;

  const minimumLeft = Math.min(
    window.innerWidth - LAUNCHER_SIZE - LAUNCHER_MARGIN,
    sidebarBoundary() + LAUNCHER_MARGIN,
  );
  const maximumLeft = Math.max(
    minimumLeft,
    window.innerWidth - LAUNCHER_SIZE - LAUNCHER_MARGIN,
  );
  const maximumTop = Math.max(
    LAUNCHER_MARGIN,
    window.innerHeight - LAUNCHER_SIZE - LAUNCHER_MARGIN,
  );

  return {
    left: clamp(position.left, minimumLeft, maximumLeft),
    top: clamp(position.top, LAUNCHER_MARGIN, maximumTop),
  };
}

function initialLauncherPosition(): LauncherPosition {
  if (typeof window === "undefined") {
    return {
      left: LAUNCHER_MARGIN,
      top: LAUNCHER_MARGIN,
    };
  }

  return fitLauncherToViewport({
    left:
      window.innerWidth -
      LAUNCHER_SIZE -
      LAUNCHER_MARGIN -
      LAUNCHER_DEFAULT_RIGHT_OFFSET,
    top:
      window.innerHeight -
      LAUNCHER_SIZE -
      LAUNCHER_MARGIN -
      LAUNCHER_DEFAULT_BOTTOM_OFFSET,
  });
}

export function ChatDisplayContainer({
  floating,
  label,
  onChangeFloating,
  children,
}: Props) {
  const [floatingRect, setFloatingRect] = useState<FloatingRect>(initialFloatingRect);
  const [minimized, setMinimized] = useState(false);
  const [launcherPosition, setLauncherPosition] = useState<LauncherPosition>(initialLauncherPosition);
  const resizeStateRef = useRef<ResizeState | null>(null);
  const launcherDragStateRef = useRef<LauncherDragState | null>(null);
  const suppressLauncherClickRef = useRef(false);

  useEffect(() => {
    if (!floating) {
      setMinimized(false);
      return;
    }

    const handleResize = () => {
      setFloatingRect((current) => fitRectToViewport(current));
      setLauncherPosition((current) => fitLauncherToViewport(current));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [floating]);

  const startResize = (
    event: ReactPointerEvent<HTMLButtonElement>,
    corner: ResizeCorner,
  ) => {
    if (!floating || minimized) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeStateRef.current = {
      corner,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      rect: floatingRect,
    };
  };

  const resizeFloatingWindow = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const resizeState = resizeStateRef.current;
    if (!resizeState || resizeState.pointerId !== event.pointerId || typeof window === "undefined") {
      return;
    }

    event.preventDefault();

    const dx = event.clientX - resizeState.startX;
    const dy = event.clientY - resizeState.startY;
    const start = resizeState.rect;
    const next = { ...start };
    const minWidth = Math.min(MIN_FLOATING_WIDTH, window.innerWidth - FLOATING_MARGIN * 2);
    const minHeight = Math.min(MIN_FLOATING_HEIGHT, window.innerHeight - FLOATING_MARGIN * 2);

    if (resizeState.corner.includes("e")) {
      next.width = clamp(
        start.width + dx,
        minWidth,
        window.innerWidth - start.left - FLOATING_MARGIN,
      );
    }

    if (resizeState.corner.includes("w")) {
      const maxLeft = start.left + start.width - minWidth;
      next.left = clamp(start.left + dx, FLOATING_MARGIN, maxLeft);
      next.width = start.width + (start.left - next.left);
    }

    if (resizeState.corner.includes("s")) {
      next.height = clamp(
        start.height + dy,
        minHeight,
        window.innerHeight - start.top - FLOATING_MARGIN,
      );
    }

    if (resizeState.corner.includes("n")) {
      const maxTop = start.top + start.height - minHeight;
      next.top = clamp(start.top + dy, FLOATING_MARGIN, maxTop);
      next.height = start.height + (start.top - next.top);
    }

    setFloatingRect(next);
  };

  const stopResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const resizeState = resizeStateRef.current;
    if (resizeState?.pointerId === event.pointerId) {
      resizeStateRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  };

  const minimizeFloatingChat = () => {
    if (!floating) return;

    setLauncherPosition(initialLauncherPosition());
    setMinimized(true);
  };

  const restoreFloatingChat = () => {
    setFloatingRect(initialFloatingRect());
    setMinimized(false);
  };

  const startLauncherDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!floating || !minimized) return;

    event.currentTarget.setPointerCapture(event.pointerId);
    launcherDragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: launcherPosition.left,
      startTop: launcherPosition.top,
      moved: false,
    };
  };

  const dragLauncher = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const dragState = launcherDragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    event.preventDefault();
    const dx = event.clientX - dragState.startX;
    const dy = event.clientY - dragState.startY;

    if (
      !dragState.moved &&
      (Math.abs(dx) >= LAUNCHER_DRAG_THRESHOLD || Math.abs(dy) >= LAUNCHER_DRAG_THRESHOLD)
    ) {
      dragState.moved = true;
    }

    setLauncherPosition(
      fitLauncherToViewport({
        left: dragState.startLeft + dx,
        top: dragState.startTop + dy,
      }),
    );
  };

  const stopLauncherDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const dragState = launcherDragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    suppressLauncherClickRef.current = dragState.moved;
    launcherDragStateRef.current = null;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const openFromLauncher = () => {
    if (suppressLauncherClickRef.current) {
      suppressLauncherClickRef.current = false;
      return;
    }

    restoreFloatingChat();
  };

  return (
    <>
      <div
        className={
          floating
            ? "fixed z-[90] flex flex-col overflow-hidden rounded-[24px] border border-emerald-100 bg-white shadow-[0_30px_90px_rgba(15,23,42,.22)]"
            : "dashboard-fade-up dashboard-delay-3 mt-6"
        }
        style={
          floating
            ? {
                display: minimized ? "none" : undefined,
                left: `${floatingRect.left}px`,
                top: `${floatingRect.top}px`,
                width: `${floatingRect.width}px`,
                height: `${floatingRect.height}px`,
              }
            : undefined
        }
      >
        <div
          className={[
            "flex items-center justify-between gap-3 border border-[#d7e5df] bg-[linear-gradient(135deg,#ffffff_0%,#f4fbf8_100%)]",
            floating
              ? "shrink-0 rounded-t-[24px] border-b-0 px-3 py-2"
              : "rounded-[24px] px-4 py-3 shadow-[0_14px_36px_rgba(15,23,42,.06)]",
          ].join(" ")}
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              className={[
                "grid shrink-0 place-items-center border border-emerald-100 bg-emerald-50 text-emerald-700",
                floating ? "h-8 w-8 rounded-xl" : "h-9 w-9 rounded-2xl",
              ].join(" ")}
            >
              <MessageCircle size={floating ? 14 : 16} />
            </span>
            <div className="min-w-0">
              <div className="truncate text-sm font-black text-slate-950">{label}</div>
              {!floating && (
                <div className="text-xs font-semibold text-slate-500">
                  Chat acoplado al final de la página
                </div>
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            {floating && (
              <button
                type="button"
                onClick={minimizeFloatingChat}
                className="grid h-8 w-8 place-items-center rounded-xl border border-emerald-100 bg-white text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition hover:border-emerald-200 hover:bg-emerald-50"
                title="Minimizar chat"
                aria-label="Minimizar chat"
              >
                <Minus size={15} />
              </button>
            )}

            <button
              type="button"
              onClick={() => onChangeFloating(!floating)}
              className={[
                "inline-flex shrink-0 items-center gap-2 border border-emerald-100 bg-white text-xs font-black text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition hover:border-emerald-200 hover:bg-emerald-50",
                floating ? "rounded-xl px-2.5 py-1.5" : "rounded-2xl px-3 py-2",
              ].join(" ")}
            >
              {floating ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              {floating ? "Acoplar" : "Modo flotante"}
            </button>
          </div>
        </div>

        <div className={floating ? "min-h-0 flex-1 overflow-hidden p-1.5 pt-0" : ""}>
          {children}
        </div>

        {floating &&
          !minimized &&
          (["nw", "ne", "sw", "se"] as ResizeCorner[]).map((corner) => (
            <button
              key={corner}
              type="button"
              aria-label={`Redimensionar chat desde la esquina ${corner}`}
              title="Arrastra para cambiar el tamaño"
              onPointerDown={(event) => startResize(event, corner)}
              onPointerMove={resizeFloatingWindow}
              onPointerUp={stopResize}
              onPointerCancel={stopResize}
              className={[
                "absolute z-[100] h-5 w-5 touch-none bg-transparent",
                corner === "nw" ? "left-0 top-0 cursor-nwse-resize border-l-2 border-t-2 border-emerald-300/60" : "",
                corner === "ne" ? "right-0 top-0 cursor-nesw-resize border-r-2 border-t-2 border-emerald-300/60" : "",
                corner === "sw" ? "bottom-0 left-0 cursor-nesw-resize border-b-2 border-l-2 border-emerald-300/60" : "",
                corner === "se" ? "bottom-0 right-0 cursor-nwse-resize border-b-2 border-r-2 border-emerald-300/60" : "",
              ].join(" ")}
            />
          ))}
      </div>

      {floating && minimized && (
        <button
          type="button"
          onClick={openFromLauncher}
          onPointerDown={startLauncherDrag}
          onPointerMove={dragLauncher}
          onPointerUp={stopLauncherDrag}
          onPointerCancel={stopLauncherDrag}
          className="fixed z-[95] grid h-14 w-14 touch-none place-items-center rounded-full border border-white/80 bg-[linear-gradient(135deg,#10b981,#14b8a6)] text-white shadow-[0_18px_42px_rgba(16,185,129,.35)] transition-shadow hover:shadow-[0_22px_50px_rgba(16,185,129,.45)] active:cursor-grabbing"
          style={{
            left: `${launcherPosition.left}px`,
            top: `${launcherPosition.top}px`,
            cursor: launcherDragStateRef.current ? "grabbing" : "grab",
          }}
          title="Abrir chat flotante"
          aria-label="Abrir chat flotante"
        >
          <MessageCircle size={24} />
        </button>
      )}
    </>
  );
}
